/**
 * The member app's four destinations.
 *
 * Nine flat links behind a hamburger is what this replaces. The routes
 * themselves are untouched — every existing URL, bookmark and redirect still
 * resolves — this only decides which of them belong together and which one a
 * tab lands on.
 */

import { DISCIPLINES, type Discipline } from '@structural-climbing/validation'

export type NavGroupId = 'home' | 'climb' | 'community' | 'me'

/** The URL segment a discipline's pages live under: `/:gymSlug/bouldering/routes`. */
export function disciplinePath(discipline: Discipline): 'bouldering' | 'lead' {
  return discipline === 'lead' ? 'lead' : 'bouldering'
}

/** The discipline a URL segment names, or null when it is not a discipline. */
export function disciplineForPath(segment: string | undefined): Discipline | null {
  return segment === 'bouldering' ? 'boulder' : segment === 'lead' ? 'lead' : null
}

const DISCIPLINE_LABEL_KEYS: Record<Discipline, string> = {
  boulder: 'routes.disciplineBouldering',
  lead: 'routes.disciplineLead',
}

export interface NavGroup {
  id: NavGroupId
  /** i18n key for the tab label. */
  labelKey: string
  /** Where the tab goes when nothing better is remembered. */
  primary: (gymSlug: string | null) => string | null
  /** Sub-destinations, shown as pills under the header and nested in the sidebar. */
  items: (gymSlug: string | null) => NavItem[]
}

/** The dot sources the nav knows about. */
export type DotKey = 'profile' | 'news' | 'events' | 'competitions'

export interface NavItem {
  to: string
  labelKey: string
  /** Which badge count, if any, rides on this item. */
  badge?: 'events' | 'news' | 'competitions'
  /**
   * Which dot, if any, rides on this item.
   *
   * A dot rather than a number: "there is something here" is all a thumb needs,
   * and the desktop sidebar, which shows every destination at once, keeps the
   * count. News and Events carry both, and each surface picks the one it wants.
   */
  dot?: DotKey
  /** The wall a discipline parent stands for, so the sidebar can draw its glyph. */
  discipline?: Discipline
  /**
   * Destinations that live under this one. The parent is a single pill in the
   * header; its children are switched from the bar above the tab bar, where a
   * thumb already is.
   */
  children?: NavItem[]
}

/**
 * The four destinations for a gym with these disciplines. A bouldering-only gym
 * gets one pill under Climb, not a Lead pill that opens an empty wall.
 */
export function navGroups(
  disciplines: readonly Discipline[] = DISCIPLINES,
  { classes = false }: { classes?: boolean } = {},
): NavGroup[] {
  return [
    {
      id: 'home',
      labelKey: 'nav.home',
      primary: (slug) => (slug ? `/${slug}/dashboard` : null),
      // Classes sit with the membership: only someone whose plan includes an
      // instructor can book one, so nobody else gets a pill that leads nowhere.
      items: (slug) =>
        slug
          ? [
              { to: `/${slug}/dashboard`, labelKey: 'dashboard.menuLink' },
              ...(classes ? [{ to: `/${slug}/classes`, labelKey: 'classes.menuLink' }] : []),
            ]
          : [],
    },
    {
      id: 'climb',
      labelKey: 'nav.climb',
      // Competitions span gyms and are open to a member who belongs to none, so
      // this tab is the one that survives without a gym.
      primary: (slug) =>
        slug ? `/${slug}/${disciplinePath(disciplines[0])}/routes` : '/competitions',
      items: (slug) => [
        // One pill per discipline the gym has. Bouldering and lead are graded
        // and scored on different scales, so each has its own wall and its own
        // ranking. Progress sits beside them because it spans both.
        ...(slug
          ? [
              ...disciplines.map((d) => ({
                to: `/${slug}/${disciplinePath(d)}`,
                labelKey: DISCIPLINE_LABEL_KEYS[d],
                discipline: d,
                children: [
                  { to: `/${slug}/${disciplinePath(d)}/routes`, labelKey: 'routes.menuLink' },
                  { to: `/${slug}/${disciplinePath(d)}/standings`, labelKey: 'standings.menuLink' },
                ],
              })),
              { to: `/${slug}/progress`, labelKey: 'progress.menuLink' },
            ]
          : []),
        {
          to: '/competitions',
          labelKey: 'competitions.menuLink',
          badge: 'competitions' as const,
          dot: 'competitions' as const,
        },
      ],
    },
    {
      id: 'community',
      // What the gym is saying, rather than what you are climbing. Standings and
      // competitions are scores, so they sit with the climbing.
      primary: (slug) => (slug ? `/${slug}/news` : null),
      labelKey: 'nav.community',
      items: (slug) =>
        slug
          ? [
              {
                to: `/${slug}/news`,
                labelKey: 'news.menuLink',
                badge: 'news' as const,
                dot: 'news' as const,
              },
              {
                to: `/${slug}/events`,
                labelKey: 'events.menuLink',
                badge: 'events' as const,
                dot: 'events' as const,
              },
            ]
          : [],
    },
    {
      id: 'me',
      labelKey: 'nav.me',
      primary: () => '/profile',
      // Familie needs a gym: a child's plan and code belong to one.
      items: (slug) => [
        { to: '/profile', labelKey: 'profile.menuLink', dot: 'profile' },
        ...(slug ? [{ to: `/${slug}/family`, labelKey: 'family.menuLink' }] : []),
        { to: '/our-gyms', labelKey: 'ourGyms.menuLink' },
      ],
    },
  ]
}

/** The destinations of a gym with both disciplines. */
export const NAV_GROUPS: NavGroup[] = navGroups()

/**
 * Which tab should look selected for a given URL.
 *
 * Matches on the path's own shape rather than against the member's gym slug,
 * so it keeps working on a gym the memberships list has not loaded yet — a real
 * case on first paint, and the reason the old sidebar used to flicker.
 */
export function groupForPath(pathname: string): NavGroupId | null {
  // Global routes first: these carry no slug and would otherwise fall through
  // to the gym-scoped matching below.
  if (pathname.startsWith('/competitions')) return 'climb'
  if (pathname.startsWith('/profile')) return 'me'
  if (pathname.startsWith('/our-gyms')) return 'me'

  // The join flows name a gym in the URL but the visitor is not a member of it
  // yet, so they must not light up a tab.
  if (pathname.startsWith('/join/') || pathname.startsWith('/guest/')) return null

  const segment = pathname.split('/')[2]
  switch (segment) {
    case 'dashboard':
    case 'classes':
    case 'gym':
    case 'ideas':
      return 'home'
    // `routes` and `standings` are the pre-split URLs, on their way to a redirect.
    case 'bouldering':
    case 'lead':
    case 'progress':
    case 'routes':
    case 'standings':
      return 'climb'
    case 'news':
    case 'events':
      return 'community'
    case 'family':
      return 'me'
    default:
      return null
  }
}

/** True when a path is this destination or something nested under it. */
export function itemMatchesPath(to: string, pathname: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`)
}

/**
 * Every destination in a list, with nested ones lifted to the top level.
 *
 * The sidebar shows all of them — a mouse has no reason to pay for a second
 * click — and "have I been here before" checks need the full set.
 */
export function flattenItems(items: NavItem[]): NavItem[] {
  return items.flatMap((item) => item.children ?? [item])
}

/**
 * Which top-level item owns the current path.
 *
 * A parent is owned by its children's paths too: standing on Progress has to
 * light up the pill that holds it, not none of them.
 */
export function activeItem(items: NavItem[], pathname: string): NavItem | null {
  return (
    items.find(
      (item) =>
        itemMatchesPath(item.to, pathname) ||
        item.children?.some((child) => itemMatchesPath(child.to, pathname)),
    ) ?? null
  )
}

const REMEMBERED_KEY = 'sc:nav-group-route'

/**
 * Where a tab should go, preferring wherever you were last time.
 *
 * Tapping Community after reading the standings should return you to the
 * standings, not to news. Same reasoning as `useTabParam`: state that survives
 * a reload, which in an installed PWA is every pull-to-refresh.
 */
export function routeForGroup(
  group: NavGroup,
  gymSlug: string | null,
  remembered = readRemembered(),
): string | null {
  const items = flattenItems(group.items(gymSlug))
  const last = remembered[group.id]
  if (last && items.some((item) => item.to === last)) return last
  return group.primary(gymSlug)
}

export function rememberRoute(group: NavGroupId, pathname: string) {
  const next = { ...readRemembered(), [group]: pathname }
  try {
    sessionStorage.setItem(REMEMBERED_KEY, JSON.stringify(next))
  } catch {
    // Private mode and storage-blocked browsers: the nav still works, it just
    // always lands on each group's primary route.
  }
}

export function readRemembered(): Partial<Record<NavGroupId, string>> {
  try {
    const raw = sessionStorage.getItem(REMEMBERED_KEY)
    return raw ? (JSON.parse(raw) as Partial<Record<NavGroupId, string>>) : {}
  } catch {
    return {}
  }
}

/**
 * The tabs to actually show.
 *
 * Without a gym there is no dashboard, no routes and no standings to link to.
 * Those tabs are dropped rather than pointed at `/`, which is what the old
 * sidebar did and what made every one of them look like a working link that
 * quietly went nowhere.
 */
export function visibleGroups(
  gymSlug: string | null,
  {
    childAccount = false,
    disciplines = DISCIPLINES,
    classes = false,
  }: { childAccount?: boolean; disciplines?: readonly Discipline[]; classes?: boolean } = {},
): NavGroup[] {
  const groups = navGroups(disciplines, { classes })
  // A child's own login gets the climbing and nothing around it: no dashboard
  // (which is the membership and the QR), no news, and no gym list to join from.
  if (childAccount)
    return groups
      .filter((g) => g.id === 'climb' || g.id === 'me')
      .map((g) =>
        g.id === 'me'
          ? {
              ...g,
              // A child's login has no gym list to join from and no family to run.
              items: (slug) =>
                g
                  .items(slug)
                  .filter((item) => item.to !== '/our-gyms' && !item.to.endsWith('/family')),
            }
          : g,
      )
  if (gymSlug) return groups
  // Climb keeps competitions, which are open to anyone; Home and Community have
  // nothing to point at without a gym.
  return groups.filter((g) => g.id === 'climb' || g.id === 'me')
}
